// =============================================================================
// Un compteur listé par la fiche bâtiment au seul titre d'une demande de
// consentement en cours (DEV-2311, retour de la campagne du 07/10/2026).
//
// Ce que le client doit vivre : avant tout lancement, la page lui dit que la
// collecte de ce compteur n'est pas encore active et ce qui la rend active,
// au lieu de lui proposer une étude qui ne trouverait aucune donnée.
//
// D'où vient le critère (api-metier, lu sur origin/main le 07/10/2026) :
// - `GET /api/metier/building/:id` (`BuildingRetrievalService.findById`, options
//   `consent`, `source`, `_id_user`) sert les lignes de `Building.sources`
//   (schéma `BuildingSourceSchema` : `isUsed` booléen, défaut `true`), puis,
//   pour une société à l'abonnement actif, AJOUTE les compteurs des demandes
//   de consentement en cours absents du bâtiment
//   (`ConsentRequestService.getRequestsByBuilding` : SIGN ou SHARE, la plus
//   récente par source, ni REMOVED ni EXPIRED ni TERMINATED). Ces entrées
//   ajoutées portent `consentrequest` (`state`, …) et `consentement`, mais
//   aucun champ du schéma `BuildingSourceSchema` : pas de `isUsed`.
// - Une ligne de `Building.sources` peut AUSSI porter un `consentrequest`
//   (une demande en cours sur un compteur déjà collecté) : la seule présence
//   de la demande ne dit donc rien de la collecte.
//
// Le magasin du bâtiment (`building-v2.store`, `getSources`) recopie `isUsed`
// depuis la ligne de la fiche : il vaut `undefined` pour une entrée ajoutée.
// =============================================================================

/** États d'une demande qui n'a pas (encore) rendu la collecte active. */
const ETATS_EN_ATTENTE = new Set(["CREATED", "REFUSED"])

type CompteurLu = {
    isUsed?: unknown
    consentrequest?: { state?: string | null } | null
    consentement?: { signed?: unknown } | null
}

/**
 * Vrai quand le compteur n'est listé qu'au titre d'une demande de consentement
 * non signée (créée ou refusée) : absent des sources du bâtiment, sans
 * consentement signé. Un compteur des sources du bâtiment n'est jamais « en
 * attente », même s'il porte une demande en cours.
 */
export function compteurEnAttenteDeConsentement(
    compteur: CompteurLu | null | undefined
): boolean {
    if (!compteur) return false
    if (typeof compteur.isUsed === "boolean") return false
    const etat = compteur.consentrequest?.state
    if (!etat || !ETATS_EN_ATTENTE.has(etat)) return false
    return compteur.consentement?.signed !== true
}

/** La mention du sélecteur, après le numéro du compteur. */
export const MENTION_CONSENTEMENT_EN_ATTENTE = "consentement en attente"
